import React, { useMemo, useState, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { Loader } from '../components/Components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import './gene.css';

// Searchable Select Component - Same as GenePage.js
const SearchableSelect = React.memo(({ options = [], value, onChange, placeholder, label }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Use useMemo instead of useEffect + state for filtering to avoid extra renders
    const filteredOptions = useMemo(() => {
        if (!searchTerm) return options;
        return options.filter(option =>
            option.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [searchTerm, options]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.searchable-select')) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleSelect = (option) => {
        onChange(option);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="searchable-select">
<div className="searchable-select-input" onClick={() => setIsOpen(true)}>
                <input
                    type="text"
                    value={isOpen ? searchTerm : value || ''}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={placeholder}
                    onFocus={() => setIsOpen(true)}
                    className="search-input"
                />
                <svg className="dropdown-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>

            {isOpen && (
                <div className="searchable-select-dropdown">
                    <div className="dropdown-content">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option, index) => (
                                <div
                                    key={index}
                                    className={`dropdown-option ${option === value ? 'selected' : ''}`}
                                    onClick={() => handleSelect(option)}
                                >
                                    {option}
                                </div>
                            ))
                        ) : (
                            <div className="dropdown-no-results">No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
});

// Enhanced Data Search Bar
const DataSearchBar = ({ onSearch, placeholder = "Search pathways, diseases, genes..." }) => {
    const [searchValue, setSearchValue] = useState('');

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchValue(value);
        onSearch(value);
    };

    return (
        <div className="gene-glass-card" style={{ marginBottom: '20px' }}>
            <div className="gene-card-title">
                Filter Data
            </div>
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    value={searchValue}
                    onChange={handleSearchChange}
                    placeholder={placeholder}
                    style={{
                        width: '100%',
                        padding: '12px 16px',
                        fontSize: '14px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '5vh',
                        outline: 'none',
                        transition: 'border-color 0.2s',
                        background: '#f9fafb',
                        boxSizing: 'border-box'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                    onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
                <svg 
                    style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: '20px',
                        height: '20px',
                        color: '#6b7280'
                    }}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            </div>
        </div>
    );
};

// Clean DataTable component - No ID/Description, hoverable core enrichment
const CleanDataTable = ({ data, title, isDynamic = false }) => {
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Sort data based on current sort config - MUST be before early return
    const sortedData = useMemo(() => {
        if (!data || !sortConfig.key) return data || [];

        return [...data].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            // Handle numbers
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
            }

            // Handle strings
            const aStr = String(aVal || '').toLowerCase();
            const bStr = String(bVal || '').toLowerCase();

            if (sortConfig.direction === 'asc') {
                return aStr.localeCompare(bStr);
            } else {
                return bStr.localeCompare(aStr);
            }
        });
    }, [data, sortConfig]);

    // Function to capitalize and format column names
    const formatColumnName = (header) => {
        return header
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Sorting function
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    if (!data || data.length === 0) {
        return (
            <div className="gene-glass-card" style={{ height: '100%' }}>
                <div className="gene-card-title">
                    {title}
                    <span className="gene-table-count">0 items</span>
                </div>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                    No data available
                </div>
            </div>
        );
    }

    const headers = Object.keys(data[0]);
    // Remove ID, Description, setSize, and comparison columns, put disease first
    let filteredHeaders = headers.filter(
          h => !['Description', 'setSize', 'comparison'].includes(h)
        );

        filteredHeaders = [
              ...(filteredHeaders.includes('ID') ? ['ID'] : []),
              ...(filteredHeaders.includes('disease') ? ['disease'] : []),
              ...filteredHeaders.filter(h => h !== 'ID' && h !== 'disease')
            ];
    
    return (
        <div className="gene-glass-card" style={{ height: '100%' }}>
            <div className="gene-card-title">
                {title}
                <span className="gene-table-count">{data.length} items</span>
            </div>
            <div className={`gene-table-wrapper ${isDynamic ? 'gene-table-dynamic' : ''}`} style={{ maxHeight: 'calc(100%)', overflow: 'auto', position: 'relative' }}>
                <table className="gene-enhanced-table" style={{ tableLayout: 'fixed', width: '100%' }}>
                    <colgroup>
                        {filteredHeaders.map(header => (
                            <col
                                key={header}
                                style={{
                                    width: 
                                    header === 'disease' ? '9%' :
                                    header === 'ID' ? '15%' :
                                           header === 'core_enrichment' ? '8%' :
                                           header === 'rank' ? '6%' :
                                           'auto'
                                }}
                            />
                        ))}
                    </colgroup>
                    <thead>
                        <tr>
                            {filteredHeaders.map(header => (
                                <th
                                    key={header}
                                    style={{
                                        cursor: 'pointer',
                                        userSelect: 'none',
                                        position: 'relative',
                                        padding: header === 'core_enrichment' ? '8px 4px' : '8px 8px',
                                        width: header === 'disease' ? '15%' :
                                               header === 'core_enrichment' ? '8%' :
                                               header === 'rank' ? '6%' :
                                               'auto',
                                        textAlign: header === 'core_enrichment' ? 'center' : 'left'
                                    }}
                                    onClick={() => handleSort(header)}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                    {header === 'ID' ? 'Pathway' : formatColumnName(header)}
                                        <span style={{
                                            fontSize: '12px',
                                            opacity: sortConfig.key === header ? 1 : 0.3,
                                            marginLeft: '2px'
                                        }}>
                                            {sortConfig.key === header && sortConfig.direction === 'desc' ? '↓' : '↑'}
                                        </span>
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr style={{ height: '2px' }}><td colSpan={filteredHeaders.length} style={{ padding: 0, border: 'none' }}></td></tr>
                        {sortedData.map((row, rowIndex) => (
                            <tr key={rowIndex}>
                                {filteredHeaders.map(header => (
                                    <td
                                        key={header}
                                        className="gene-table-cell"
                                        style={{
                                            fontWeight: ['ID', 'disease'].includes(header) ? 'bold' : 'normal',
                                            color: ['ID', 'disease'].includes(header) ? '#6b7280' : 'inherit',
                                            width:
                                              ['ID', 'disease'].includes(header) ? '25%' :
                                              header === 'core_enrichment' ? '8%' :
                                              header === 'rank' ? '6%' :
                                              'auto',
                                            wordWrap: 'break-word',
                                            overflow: 'hidden',
                                            textAlign: header === 'core_enrichment' ? 'center' : 'left',
                                            padding: header === 'core_enrichment' ? '8px 4px' : '8px'
                                          }}
                                          
                                        title={
                                            header === 'core_enrichment' && row[header]
                                                ? `Core Enrichment Genes: ${row[header].split('/').join(', ')}`
                                                : String(row[header] || '')
                                        }
                                    >
                                        {header === 'core_enrichment' ? (
                                            row[header] ? (
                                                <FontAwesomeIcon icon={faQuestionCircle} style={{ color: '#6b7280', fontSize: '14px' }} />
                                            ) : '-'
                                        ) : typeof row[header] === 'number' ? (
                                            Number(row[header]).toFixed(4)
                                        ) : (
                                            String(row[header] || '')
                                        )}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>


            </div>
        </div>
    );
};

function PathwayPage({ 
    selections, 
    handleSelection, 
    options, 
    data, 
    isTabLoading 
}) {
    const [searchFilter, setSearchFilter] = useState('');

    // Current mode for button highlighting
    const currentMode = selections.pathwayAnalysisMode || 'pathway';

    // Handle mode switching - reset to first option to mimic "first time" behavior
    const handleModeChange = (mode) => {
        handleSelection('pathwayAnalysisMode', mode);

        // Reset to first option to always feel like first time
        if (mode === 'pathway') {
            handleSelection('pathway', options.pathways?.[0] || ''); // Reset to first pathway
        } else {
            handleSelection('pathwayDisease', options.diseases?.[0] || ''); // Reset to first disease
        }
    };

    // Check if we have the required data for the current mode
    const hasRequiredData = useMemo(() => {
        if (currentMode === 'pathway') {
            return !!(data?.pathwayDiseases);
        } else {
            return !!(data?.diseasePathways);
        }
    }, [data, currentMode]);

    // Simple loading logic
    const shouldShowLoading = isTabLoading || !hasRequiredData;

    // Filtered and sorted data based on search
    const filteredData = useMemo(() => {
        if (!data) return {};

        const result = {};

        if (currentMode === 'pathway') {
            let pathwayData = data.pathwayDiseases ? [...data.pathwayDiseases] : [];
            
            // Apply search filter
            if (searchFilter) {
                pathwayData = pathwayData.filter(item => 
                    (item.disease || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
                    (item.ID || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
                    (item.Description || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
                    (item.core_enrichment || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
                    (item.leading_edge || '').toLowerCase().includes(searchFilter.toLowerCase())
                );
            }
            
            result.pathwayDiseases = pathwayData.sort((a, b) => {
                const aVal = a.NES || 0;
                const bVal = b.NES || 0;

                // First sort by positive/negative
                if (aVal >= 0 && bVal < 0) return -1; // positive first
                if (aVal < 0 && bVal >= 0) return 1;  // negative second

                // For positive values: longest first (descending)
                if (aVal >= 0 && bVal >= 0) return bVal - aVal;

                // For negative values: shortest first (ascending, closest to zero first)
                if (aVal < 0 && bVal < 0) return bVal - aVal;

                return 0;
            });
        } else {
            let diseaseData = data.diseasePathways ? [...data.diseasePathways] : [];
            
            // Apply search filter
            if (searchFilter) {
                diseaseData = diseaseData.filter(item => 
                    (item.ID || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
                    (item.Description || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
                    (item.core_enrichment || '').toLowerCase().includes(searchFilter.toLowerCase()) ||
                    (item.leading_edge || '').toLowerCase().includes(searchFilter.toLowerCase())
                );
            }
            
            result.diseasePathways = diseaseData
                .filter(d => d['p.adjust'] < 0.05)
                .sort((a, b) => {
                    const aVal = a.NES || 0;
                    const bVal = b.NES || 0;

                    // First sort by positive/negative
                    if (aVal >= 0 && bVal < 0) return -1; // positive first
                    if (aVal < 0 && bVal >= 0) return 1;  // negative second

                    // For positive values: longest first (descending)
                    if (aVal >= 0 && bVal >= 0) return bVal - aVal;

                    // For negative values: shortest first (ascending, closest to zero first)
                    if (aVal < 0 && bVal < 0) return bVal - aVal;

                    return 0;
                });
        }

        return result;
    }, [data, currentMode, searchFilter]);

    // Clean plot configuration
    const plotConfig = {
        displayModeBar: false,
        responsive: true
    };

    const plotLayout = {
        font: { family: '-apple-system, BlinkMacSystemFont, Segoe UI, sans-serif', size: 11 },
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(0,0,0,0)',
        margin: { l: 200, r: 20, t: 20, b: 50 }
    };

    return (
        <div className="gene-page">
            {/* Sidebar */}
            <div className="gene-sidebar">
                <h1 className="gene-sidebar-title">Pathway Level Analysis</h1>
                
                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">Analysis Mode</p>
                    <div className="gene-mode-controls">
                        <button
                            className={`gene-mode-btn ${currentMode === 'pathway' ? 'active' : ''}`}
                            onClick={() => handleModeChange('pathway')}
                        >
                            Pathway Specific
                        </button>
                        <button
                            className={`gene-mode-btn ${currentMode === 'disease' ? 'active' : ''}`}
                            onClick={() => handleModeChange('disease')}
                        >
                            Disease Specific
                        </button>
                    </div>
                </div>

                <div className="gene-sidebar-section">
                    <p className="gene-sidebar-label">
                        {currentMode === 'pathway' ? 'Select Pathway' : 'Select Disease'}
                    </p>
                    <SearchableSelect
                        key={currentMode} // Force re-mount when mode changes to avoid state conflicts
                        options={currentMode === 'pathway' ? (options.pathways || []) : (options.diseases || [])}
                        value={currentMode === 'pathway' ? selections.pathway : selections.pathwayDisease}
                        onChange={(value) => handleSelection(
                            currentMode === 'pathway' ? 'pathway' : 'pathwayDisease',
                            value
                        )}
                        placeholder={`Search ${currentMode === 'pathway' ? 'pathways' : 'diseases'}...`}
                    />
                </div>
            </div>

            {/* Main Content */}
            <div className="gene-main-content">
                {/* Loading State */}
                {shouldShowLoading && (
                    <div className="gene-loading">
                        <div className="gene-loading-spinner"></div>
                        Loading pathway analysis data...
                    </div>
                )}

                {/* Content for both modes */}
                {!shouldShowLoading && (
                    <div className="gene-content">
                        {/* Search Bar Above Everything */}
                        <DataSearchBar 
                            onSearch={setSearchFilter}
                            placeholder={`Search pathways, diseases, genes...`}
                        />

                        {/* Column Layout: Graph Top, Table Bottom */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                            {/* Top - Graph */}
                            <div className="gene-glass-card">
                                <div className="gene-card-title">
                                    {currentMode === 'pathway' ? 'Enrichment Scores Across Diseases' : 'Pathway Enrichment Scores'}
                                    <span className="gene-tag">
                                        {currentMode === 'pathway' ? selections.pathway : selections.pathwayDisease}
                                    </span>
                                </div>
                                <p className="gene-card-subtitle">
                                    {currentMode === 'pathway' 
                                        ? 'Normalized Enrichment Score (NES) values across diseases' 
                                        : 'Significantly enriched pathways (p-adj < 0.05)'
                                    }
                                    {searchFilter && <span style={{ color: '#3b82f6' }}> • Filtered: "{searchFilter}"</span>}
                                </p>
                                
                                <div 
                                    className="gene-plot-container" 
                                    style={{
                                        height: `${Math.max(250, (currentMode === 'pathway'
                                            ? (filteredData.pathwayDiseases?.length || 0) * 18 + 80
                                            : (filteredData.diseasePathways?.length || 0) * 18 + 80
                                        ))}px`,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <Plot 
                                        data={[{ 
                                            y: currentMode === 'pathway' 
                                                ? filteredData.pathwayDiseases?.map(d => d.disease)
                                                : filteredData.diseasePathways?.map(d => d.ID), 
                                            x: currentMode === 'pathway'
                                                ? filteredData.pathwayDiseases?.map(d => d.NES)
                                                : filteredData.diseasePathways?.map(d => d.NES), 
                                            type: 'bar', 
                                            orientation: 'h',
                                            marker: {
                                                color: currentMode === 'pathway'
                                                    ? filteredData.pathwayDiseases?.map(d => d.NES > 0 ? '#fca5a5' : '#7dd3fc')
                                                    : filteredData.diseasePathways?.map(d => d.enrichmentScore > 0 ? '#fca5a5' : '#7dd3fc'),
                                                opacity: 0.8,
                                                line: {
                                                    color: currentMode === 'pathway'
                                                        ? filteredData.pathwayDiseases?.map(d => d.NES > 0 ? '#ef4444' : '#0ea5e9')
                                                        : filteredData.diseasePathways?.map(d => d.enrichmentScore > 0 ? '#ef4444' : '#0ea5e9'),
                                                    width: 1
                                                },
                                                cornerradius: 20
                                            },
                                            hovertemplate: '<b>%{y}</b><br>NES: %{x:.3f}<extra></extra>'
                                        }]} 
                                        layout={{ 
                                            ...plotLayout,
                                            height: Math.max(250, (currentMode === 'pathway'
                                                ? (filteredData.pathwayDiseases?.length || 0) * 18 + 80
                                                : (filteredData.diseasePathways?.length || 0) * 18 + 80
                                            )),
                                            xaxis: { 
                                                title: 'Normalized Enrichment Score (NES)', 
                                                titlefont: { size: 12 },
                                                zeroline: true,
                                                zerolinecolor: 'rgba(0,0,0,0.3)',
                                                zerolinewidth: 1,
                                                showgrid: true,
                                                gridcolor: 'rgba(0,0,0,0.05)'
                                            },
                                            yaxis: { 
                                                tickfont: { size: 10 },
                                                showgrid: false
                                            }
                                        }} 
                                        config={plotConfig}
                                        useResizeHandler={true}
                                        style={{ width: '100%', height: '100%' }}
                                    />
                                </div>
                            </div>

                            {/* Bottom - Table */}
                            <CleanDataTable 
                                data={currentMode === 'pathway' ? filteredData.pathwayDiseases : filteredData.diseasePathways} 
                                title={currentMode === 'pathway' ? 'Pathway Enrichment Data' : 'Disease Pathway Data'} 
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default PathwayPage;
